import api from 'config/api'

import type {
  ApplicationEntryFormProps,
  BankTransfer,
  Billing,
  BillingForm,
  ContactEmails,
  DepartmentSettingsProps,
  EntryPlanCategory,
  InformationToMed,
  MedicalInstitutionProps,
  PreReservation,
  PreReservationForm,
  PreReservationListItemProps,
  PrescriptionSetting,
  PublicInformation,
  ReservationDetailProps,
  ReservationListItem,
  UpdatePasswordParams,
} from 'types/med'
import type {
  CallMethodNames,
  CallMethodVideoAppNames,
  DepartmentListItemProps,
  PharmacyProps,
  PharmacySearchApiParams,
  EligibilityVerificationUrlParams,
} from 'types/common'

import { client, searchParams } from '../client'
import { ChangeAddressFormProps } from 'ui/partials/ChangeAddressModalContent'
import { partnerCompanyProps } from 'types/med'

/*
 * 施設登録
 */
export const postApplicationEntry = (params: {
  closed: boolean
  data: ApplicationEntryFormProps
}) => {
  const url = params.closed
    ? api.applicationInstitutions
    : api.applicationInstitutionsOpen
  return client<void>(url, params.data, 'POST')
}

export const getPartnerCompanies = () =>
  client<partnerCompanyProps[]>(api.partnerCompanies)

/*
 * ログイン、パスワード
 */

export const postLogin = (data: { email: string; password: string }) =>
  client<void>(api.login, data, 'POST')

export const postForgotPassword = (data: { email: string }) =>
  client<void>(api.tempPassword, data, 'POST')

export const getLogout = () => client<void>(api.logout)

export const putPassword = (data: UpdatePasswordParams) =>
  client<void>(api.password, data, 'PUT')

/*
 * 申込み設定
 */

export const getEntryPlans = (mode: 'direct' | 'partner') =>
  client<EntryPlanCategory[]>(
    mode === 'direct' ? api.entryPlanDirect : api.entryPlanPartner
  )

/*
 * 医療機関
 */

export const getMedicalInstitutions = () =>
  client<MedicalInstitutionProps>(api.medicalInstitutions)

/*
 * 予約リスト
 */

export const getReservationList = (params: {
  reservation_date: string
  offset: number | null
  limit: number | null
}) => {
  let url = api.reservations
  return client<ReservationListItem[]>(`${url}?${searchParams(params)}`)
}

/*
 * 予約
 */

export const getReservationDetail = (params: { reservation_id: number }) => {
  const url = `${api.reservations}/${String(params.reservation_id)}`
  return client<ReservationDetailProps>(`${url}`)
}

export const getReservations = (params: {
  reservation_date?: string
  not_finished_call?: CallMethodNames // NOTE: 指定の通話方法で通話完了していない予約
  is_completed?: boolean // NOTE true:決済あり科目で請求済の予約 or 決済なし科目で完了した予約, false:決済あり科目で未請求の予約 or 決済なし科目で未完了の予約
  department_id?: number
  offset: number
  limit: number
}) => client<ReservationListItem[]>(api.reservations, params)

export const putReservationCancel = (reservation_id: number) =>
  client<void>(`${api.reservationCancel}/${String(reservation_id)}`, {}, 'PUT')

export const postReservationsCall = (
  reservation_id: number,
  is_finish: boolean
) =>
  client<void>(
    `${api.reservationsCall}/${String(reservation_id)}`,
    { is_finish },
    'POST'
  )

/*
 * 仮予約
 */

export const getPreReservationList = (
  data: {
    // NOTE: 現在は使わない設定値
    offset?: number
    limit?: number
  } = {
    offset: 0,
    limit: 1000,
  }
) => client<PreReservationListItemProps[]>(api.preReservations, data)

export const getPreReservation = (id: PreReservation['id']) =>
  client<PreReservation>(`${api.preReservations}/${id}`)

export const postPreReservation = (data: PreReservationForm) =>
  client<void>(api.preReservations, data, 'POST')

export const putPreReservation = (id: PreReservation['id']) =>
  client<void>(`${api.preReservations}/${id}`, {}, 'PUT')

export const deletePreReservation = (id: PreReservation['id']) =>
  client<void>(`${api.preReservations}/${id}`, {}, 'DELETE')

/*
 * OAuthのclient_idを取得
 */

export const getAppClientId = (type: 'stripe' | 'zoom' | 'teams') => {
  let url = ''
  switch (type) {
    case 'stripe':
      url = `${api.stripeClientId}`
      break
    case 'zoom':
      url = `${api.zoomClientId}`
      break
    case 'teams':
      url = `${api.teamsClientId}`
      break
  }
  return client<{ client_id: string }>(url)
}

/*
 * Stripe
 */

export const postStripeStandardAccounts = (data: { code?: string }) =>
  client<void>(api.stripeStandardAccounts, data, 'POST')

export const deleteStripeStandardAccounts = () =>
  client<void>(api.stripeStandardAccounts, {}, 'DELETE')

/*
 * VideoApp
 */

export const postVideoAppConnections = ({
  app,
  department_id,
  data,
}: {
  app: CallMethodVideoAppNames
  department_id: number
  data: { code?: string }
}) => {
  let baseUrl: string
  switch (app) {
    case 'video_zoom':
      baseUrl = api.zoomConnections
      break
    case 'video_teams':
      baseUrl = api.teamsConnections
      break
  }
  const url = `${baseUrl}/${department_id}`
  return client<void>(url, data, 'POST')
}

export const deleteVideoAppConnections = ({
  app,
  department_id,
}: {
  app: CallMethodVideoAppNames
  department_id: number
}) => {
  let baseUrl: string
  switch (app) {
    case 'video_zoom':
      baseUrl = api.zoomConnections
      break
    case 'video_teams':
      baseUrl = api.teamsConnections
      break
  }
  const url = `${baseUrl}/${department_id}`
  return client<void>(url, {}, 'DELETE')
}

/*
 * VC
 */

export const getVcUrl = (
  app: CallMethodVideoAppNames,
  reservation_id: number
) => {
  let baseUrl: string
  switch (app) {
    case 'video_zoom':
      baseUrl = api.videoMeetings
      break
    case 'video_teams':
      baseUrl = api.teamsMeetings
      break
  }
  const url = `${baseUrl}/${String(reservation_id)}`
  return client<{ url: string }>(url)
}

/*
 * 科目
 */

export const putDepartmentDisplayOrder = (
  id: DepartmentListItemProps['id'],
  display_order: DepartmentListItemProps['display_order']
) =>
  client<void>(`${api.departmentDisplayOrder}/${id}`, { display_order }, 'PUT')

export const getDepartmentSettings = (id: DepartmentSettingsProps['id']) =>
  client<DepartmentSettingsProps>(`${api.departmentSettings}/${id}`)

export const postDepartmentSettings = (data: DepartmentSettingsProps) =>
  client<void>(api.departmentSettings, data, 'POST')

export const putDepartmentSettings = (
  id: DepartmentSettingsProps['id'],
  data: DepartmentSettingsProps
) => client<void>(`${api.departmentSettings}/${id}`, data, 'PUT')

export const deleteDepartmentSettings = (id: DepartmentListItemProps['id']) =>
  client<DepartmentSettingsProps>(
    `${api.departmentSettings}/${id}`,
    {},
    'DELETE'
  )

/*
 * Billing
 */

export const getBilling = (reservation_id: ReservationDetailProps['id']) =>
  client<Billing[]>(`${api.billings}/${reservation_id}`)

export const postBilling = (data: BillingForm) =>
  client<void>(api.billings, data, 'POST')

/*
 * Bank
 */

export const getBankTransferSettings = () =>
  client<BankTransfer>(api.bankTransferSettings)

export const postBankTransferSettings = (data: BankTransfer) =>
  client<void>(api.bankTransferSettings, data, 'POST')

export const putBankTransferSettings = (data: BankTransfer) =>
  client<void>(api.bankTransferSettings, data, 'PUT')

// 科目設定

export const getDepartmentList = () =>
  client<DepartmentListItemProps[]>(api.departmentSettings)

// 処方設定

export const getPrescription = () =>
  client<PrescriptionSetting>(api.prescriptionMedicines)

export const putPrescription = (data: PrescriptionSetting) =>
  client<void>(api.prescriptionMedicines, data, 'PUT')

// 公開情報設定

export const getPublicInformation = () =>
  client<PublicInformation>(api.publicInformation)
export const postPublicInformation = (data: PublicInformation) =>
  client<void>(api.publicInformation, data, 'POST')

// 通知Email

export const getContactEmails = () => client<ContactEmails>(api.contactEmails)
export const putContactEmails = (data: ContactEmails) =>
  client<void>(api.contactEmails, data, 'PUT')

// 薬局検索

export const getGoogleMapsApiKey = () =>
  client<{ api_key: string }>(api.googleMapsApiKey)

export const searchPharmacies = (params: PharmacySearchApiParams) =>
  client<PharmacyProps[] | undefined>(api.pharmacies, params)

export const getPharmacies = () =>
  client<PharmacyProps[] | undefined>(api.nearPharmacies)

export const putPharmacies = (
  data: {
    scuel_id: PharmacyProps['scuel_id']
  }[]
) => client<void>(api.nearPharmacies, data, 'PUT')

export const updateApplicantAddress =
  (reservationId: number) => (data: ChangeAddressFormProps) =>
    client<void>(
      `${api.reservationsApplicantAddress}/${reservationId}`,
      data,
      'PUT'
    )

export const resendReservationMail = (id: ReservationDetailProps['id']) =>
  client<void>(`${api.reservationRemind}/${id}`)

export const updateReservationDatetime = (
  reservationId: number,
  data: {
    reservation_datetime_start: string
    reservation_datetime_end: string
  }
) => client<void>(`${api.reservationsDatetime}/${reservationId}`, data, 'PUT')

export const getInformationToMed = () =>
  client<InformationToMed>(api.informationToMed)

/**
 * オンライン資格確認
 */
export const updateEligibilityVerificationUrl = (
  data: EligibilityVerificationUrlParams
) => client<void>(api.onlineEligibilityVerificationUrl, data, 'PUT')
